import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { ROUTES } from '@/routes';
import { peopleApi } from '@/services/api';
import { Globe, Users, ArrowRight, Check } from 'lucide-react';

type Step = 'welcome' | 'household' | 'complete';

export function OnboardingPage() {
  const [step, setStep] = useState<Step>('welcome');
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="w-full max-w-lg">
        {step === 'welcome' && <WelcomeStep onNext={() => setStep('household')} />}
        {step === 'household' && <HouseholdStep onNext={() => setStep('complete')} />}
        {step === 'complete' && <CompleteStep onFinish={() => navigate(ROUTES.app.dashboard)} />}
      </div>
    </div>
  );
}

function WelcomeStep({ onNext }: { onNext: () => void }) {
  const { user } = useAuthStore();

  return (
    <div className="space-y-6 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
        <Globe className="h-9 w-9 text-primary" />
      </div>
      <div>
        <h1 className="text-2xl font-bold">Welcome, {user?.name?.split(' ')[0]}!</h1>
        <p className="mt-2 text-muted-foreground">
          Let's set up your expense tracker. This takes about 2 minutes.
        </p>
      </div>
      <div className="space-y-3 text-left">
        <div className="flex items-center gap-3 rounded-lg border border-border p-3">
          <Check className="h-5 w-5 text-green-500" />
          <span className="text-sm">Gmail connected — receipts will be scanned automatically</span>
        </div>
        <div className="flex items-center gap-3 rounded-lg border border-border p-3">
          <Users className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm">Set up your household for expense splitting</span>
        </div>
      </div>
      <button onClick={onNext} className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">
        Get started <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function HouseholdStep({ onNext }: { onNext: () => void }) {
  const [members, setMembers] = useState<Array<{ name: string; type: 'adult' | 'child' | 'infant' }>>([]);
  const [name, setName] = useState('');
  const [type, setType] = useState<'adult' | 'child' | 'infant'>('adult');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveAndContinue = async () => {
    setSaving(true);
    setError(null);
    try {
      for (const m of members) {
        await peopleApi.create({ name: m.name, relation: 'family', ageGroup: m.type, aliases: [] });
      }
      onNext();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const addMember = () => {
    if (name.trim()) {
      setMembers([...members, { name: name.trim(), type }]);
      setName('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-bold">Who travels with you?</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Add household members for automatic expense splitting. You can change this later.
        </p>
      </div>

      {members.length > 0 && (
        <div className="space-y-2">
          {members.map((m, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border border-border p-3">
              <span className="text-sm font-medium">{m.name}</span>
              <span className="rounded-full bg-accent px-2 py-0.5 text-xs">{m.type}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addMember()}
          className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <select
          value={type}
          onChange={(e) => setType(e.target.value as typeof type)}
          className="rounded-lg border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="adult">Adult</option>
          <option value="child">Child</option>
          <option value="infant">Infant</option>
        </select>
        <button onClick={addMember} className="rounded-lg bg-accent px-4 py-2 text-sm font-medium hover:bg-accent/80">
          Add
        </button>
      </div>

      <p className="text-xs text-muted-foreground">
        Next, add where each of you has lived in Settings → People & Home History (you can paste it from a spreadsheet).
      </p>

      <button
        onClick={saveAndContinue}
        disabled={saving}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
      >
        {saving ? 'Saving…' : members.length > 0 ? 'Continue' : 'Skip — just me'} <ArrowRight className="h-4 w-4" />
      </button>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

function CompleteStep({ onFinish }: { onFinish: () => void }) {
  return (
    <div className="space-y-6 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-green-100 dark:bg-green-950">
        <Check className="h-9 w-9 text-green-500" />
      </div>
      <div>
        <h2 className="text-xl font-bold">You're all set!</h2>
        <p className="mt-2 text-muted-foreground">
          We're now scanning your email history in the background. This may take a few hours for a full backfill.
          You'll get a single notification when it's complete.
        </p>
      </div>
      <div className="rounded-lg border border-border bg-card p-4 text-left text-sm">
        <p className="font-medium">What's happening now:</p>
        <ul className="mt-2 space-y-1 text-muted-foreground">
          <li>• Scanning 10+ years of email receipts and confirmations</li>
          <li>• Auto-detecting trips from location clusters</li>
          <li>• Merging duplicate emails about the same purchase</li>
        </ul>
      </div>
      <button onClick={onFinish} className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">
        Go to Dashboard <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}
